/**
 * Utilities for persisting the JSM crossflow state across signup pages.
 *
 * The `useIsJsmCrossflow` hook detects the flow origin from URL params and
 * referrer, but those signals are lost by the time the user reaches the
 * confirmation page (the URL changes to the provisioning handoff URL).
 *
 * We write the detected value to sessionStorage on every pre-confirmation
 * render so that confirmation-page screens can read it back reliably.
 */

export const JSM_CROSSFLOW_SESSION_KEY = "signup_jsm_crossflow";

export const persistJsmCrossflow = (isJsmCrossflow: boolean): void => {
    if (typeof window !== "undefined") {
        try {
            sessionStorage.setItem(
                JSM_CROSSFLOW_SESSION_KEY,
                String(isJsmCrossflow)
            );
        } catch {
            // sessionStorage may be unavailable in some environments (e.g. private
            // browsing with strict settings). Silently ignore.
        }
    }
};

export const readPersistedJsmCrossflow = (): boolean | null => {
    if (typeof window !== "undefined") {
        try {
            const stored = sessionStorage.getItem(JSM_CROSSFLOW_SESSION_KEY);
            if (stored !== null) return stored === "true";
        } catch {
            // sessionStorage unavailable — fall through to return null
        }
    }
    return null;
};

/**
 * Read-once variant: returns the persisted value and immediately removes it
 * from sessionStorage. This prevents a stale value from a previous signup
 * journey from bleeding into a subsequent signup in the same tab that arrives
 * at the confirmation page via a path that bypasses JSMOverlayTemplate.
 */
export const consumePersistedJsmCrossflow = (): boolean | null => {
    if (typeof window !== "undefined") {
        try {
            const stored = sessionStorage.getItem(JSM_CROSSFLOW_SESSION_KEY);
            if (stored !== null) {
                sessionStorage.removeItem(JSM_CROSSFLOW_SESSION_KEY);
                return stored === "true";
            }
        } catch {
            // sessionStorage unavailable — fall through to return null
        }
    }
    return null;
};
